// Store reativo para Grupos de Complementos Globais — MataFome
// Gerencia o estado local dos grupos reutilizáveis de complementos/adicionais
import { writable, get } from 'svelte/store';
import {
    fetchComplementGroups,
    createComplementGroup,
    createComplementGroupsBatch,
    updateComplementGroup,
    toggleComplementGroup,
    deleteComplementGroup,
    addComplementItem,
    updateComplementItem,
    toggleComplementItem,
    deleteComplementItem
} from '../services/api.js';

// Store principal: lista de grupos com seus itens
export const complementGroups = writable([]);

// Store de loading
export const complementsLoading = writable(false);

// ==========================================
// OPERAÇÕES DE GRUPOS
// ==========================================

/**
 * Sincroniza todos os grupos do backend para o store local
 */
export async function syncGroups() {
    complementsLoading.set(true);
    try {
        const groups = await fetchComplementGroups();
        complementGroups.set(groups);
        return groups;
    } catch (err) {
        console.error('[ComplementsStore] Erro ao sincronizar grupos:', err.message);
        throw err;
    } finally {
        complementsLoading.set(false);
    }
}

/**
 * Cria um grupo e adiciona ao store local
 */
export async function createGroup(data) {
    const group = await createComplementGroup(data);
    group.items = group.items || [];
    group.product_count = 0;
    complementGroups.update(groups => [...groups, group]);
    return group;
}

/**
 * Cria múltiplos grupos com itens em lote e adiciona ao store local
 */
export async function createGroupsBatch(batchData) {
    complementsLoading.set(true);
    try {
        const created = await createComplementGroupsBatch(batchData);
        complementGroups.update(groups => {
            const list = [...groups];
            for (const g of created) {
                g.items = g.items || [];
                g.product_count = 0;
                list.push(g);
            }
            return list;
        });
        return created;
    } finally {
        complementsLoading.set(false);
    }
}

/**
 * Atualiza um grupo e reflete no store local
 */
export async function updateGroup(groupId, data) {
    const updated = await updateComplementGroup(groupId, data);
    complementGroups.update(groups =>
        groups.map(g => g.id === groupId ? { ...g, ...updated, items: g.items, product_count: g.product_count } : g)
    );
    return updated;
}

/**
 * Alterna ativo/inativo de um grupo
 */
export async function toggleGroup(groupId) {
    const updated = await toggleComplementGroup(groupId);
    complementGroups.update(groups =>
        groups.map(g => g.id === groupId ? { ...g, active: updated.active } : g)
    );
    return updated;
}

/**
 * Exclui um grupo e remove do store local
 */
export async function removeGroup(groupId) {
    await deleteComplementGroup(groupId);
    complementGroups.update(groups => groups.filter(g => g.id !== groupId));
}

// ==========================================
// OPERAÇÕES DE ITENS
// ==========================================

/**
 * Adiciona um item a um grupo e atualiza o store local
 */
export async function addItem(groupId, data) {
    const item = await addComplementItem(groupId, data);
    complementGroups.update(groups =>
        groups.map(g => {
            if (g.id === groupId) {
                return { ...g, items: [...(g.items || []), item] };
            }
            return g;
        })
    );
    return item;
}

/**
 * Atualiza um item e reflete no store local
 */
export async function updateItem(itemId, data) {
    const updated = await updateComplementItem(itemId, data);
    complementGroups.update(groups =>
        groups.map(g => ({
            ...g,
            items: (g.items || []).map(i => i.id === itemId ? { ...i, ...updated } : i)
        }))
    );
    return updated;
}

/**
 * Alterna ativo/inativo de um item
 */
export async function toggleItem(itemId) {
    const updated = await toggleComplementItem(itemId);
    complementGroups.update(groups =>
        groups.map(g => ({
            ...g,
            items: (g.items || []).map(i => i.id === itemId ? { ...i, active: updated.active } : i)
        }))
    );
    return updated;
}

/**
 * Exclui um item e remove do store local
 */
export async function removeItem(itemId) {
    await deleteComplementItem(itemId);
    complementGroups.update(groups =>
        groups.map(g => ({
            ...g,
            items: (g.items || []).filter(i => i.id !== itemId)
        }))
    );
}

// ==========================================
// HELPERS
// ==========================================

/**
 * Retorna um grupo pelo ID a partir do estado atual do store
 */
export function getGroupById(groupId) {
    const groups = get(complementGroups);
    return groups.find(g => g.id === groupId) || null;
}
